import { useEffect, useMemo, useState } from 'react';
import { Checkbox, Flex, Text } from '@servicetitan/anvil2';
import DOMPurify from 'dompurify';
import { useDynamicDisclosures, UseDynamicDisclosuresOptions } from './useDynamicDisclosures';
import { DynamicDisclosureStore } from './DynamicDisclosureStore';
import { HtmlContent, LoadingScreen, SignatureCanvas } from '@servicetitan/financing-utils-shared';
import {
    useSecondLookIntegration,
    useProviderConfig,
} from '@servicetitan/financing-workflow-shared';
import { SecondLookCoapplicationDisclosure } from '../disclosures/SecondLookCoapplicationDisclosure';

export interface DynamicDisclosuresProps extends UseDynamicDisclosuresOptions {
    /** Callback when disclosure state changes (ready to continue or not) */
    onChange?: (canContinue: boolean, base64Signature: string | null, token: string | null) => void;
}

/**
 * Dynamic disclosures component that fetches disclosures from API
 * and provides checkbox and signature functionality
 */
export const DynamicDisclosures = ({ onChange, ...disclosureOptions }: DynamicDisclosuresProps) => {
    const { dynamicDisclosureState, updateDisclosureCheckbox, setSignature, setDisclosureToken } =
        DynamicDisclosureStore.useStore();

    const { data: disclosuresData, isLoading } = useDynamicDisclosures(disclosureOptions);

    // Initialize disclosure token when data loads
    useEffect(() => {
        if (disclosuresData?.token) {
            setDisclosureToken(disclosuresData.token);
        }
    }, [disclosuresData?.token, setDisclosureToken]);

    // Sanitize HTML content for safe rendering
    const getSanitizedText = (text: string) =>
        DOMPurify.sanitize(text, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ALLOWED_TAGS: ['a', 'i', 'b', 'ul', 'li', 'p', 'br'],
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ALLOWED_ATTR: ['href', 'target'],
        });

    const { turnsCoapplicationDisclosureText } = useProviderConfig();
    const secondLookIntegration = useSecondLookIntegration();
    const showSecondLookDisclosure =
        secondLookIntegration.isEnabled && turnsCoapplicationDisclosureText !== undefined;

    const [secondLookDisclosureChecked, setSecondLookDisclosureChecked] = useState(false);

    // Check if all required checkboxes are checked
    const allCheckboxesChecked = useMemo(() => {
        if (!disclosuresData?.disclosures) {
            return false;
        }

        const checkboxDisclosures = disclosuresData.disclosures.filter(d => d.checkBox);
        if (checkboxDisclosures.length === 0) {
            // If no main disclosures with checkboxes, only check second-look disclosure if shown
            return showSecondLookDisclosure ? secondLookDisclosureChecked : true;
        }

        const allMainDisclosuresChecked = checkboxDisclosures.every(
            disclosure => dynamicDisclosureState.checkedDisclosures[disclosure.order],
        );

        // Only require secondLookDisclosureChecked when second-look disclosure is shown
        return (
            allMainDisclosuresChecked &&
            (showSecondLookDisclosure ? secondLookDisclosureChecked : true)
        );
    }, [
        disclosuresData?.disclosures,
        dynamicDisclosureState.checkedDisclosures,
        secondLookDisclosureChecked,
        showSecondLookDisclosure,
    ]);

    // Check if signature is required and provided
    const signatureRequired = useMemo(() => {
        return disclosuresData?.disclosures?.some(d => d.signatureBox) ?? false;
    }, [disclosuresData?.disclosures]);

    const signatureProvided = signatureRequired ? dynamicDisclosureState.hasSigned : true;

    // Determine if continue button should be enabled
    const canContinue = allCheckboxesChecked && signatureProvided;

    // Find where signature disclosures start to insert SecondLookCoapplicationDisclosure before them
    const firstSignatureIndex = useMemo(() => {
        if (!disclosuresData?.disclosures) {
            return 0;
        }
        const index = disclosuresData.disclosures.findIndex(d => d.signatureBox);
        return index >= 0 ? index : disclosuresData.disclosures.length;
    }, [disclosuresData?.disclosures]);

    // Notify parent component when state changes
    useEffect(() => {
        onChange?.(
            canContinue,
            dynamicDisclosureState.base64Signature,
            dynamicDisclosureState.disclosureToken,
        );
    }, [
        canContinue,
        dynamicDisclosureState.base64Signature,
        dynamicDisclosureState.disclosureToken,
        onChange,
    ]);

    const handleCheckboxChange = (order: number, checked: boolean) => {
        updateDisclosureCheckbox(order, checked);
    };

    const handleSignatureChange = (base64Signature: string | null) => {
        setSignature(base64Signature);
    };

    if (isLoading) {
        return <LoadingScreen />;
    }

    if (!disclosuresData?.disclosures?.length) {
        return <Text>No disclosures available.</Text>;
    }

    const renderDisclosure = (disclosure: (typeof disclosuresData.disclosures)[number]) => {
        const sanitizedText = getSanitizedText(disclosure.text ?? '');
        const isChecked = dynamicDisclosureState.checkedDisclosures[disclosure.order] ?? false;

        return (
            <div key={`${disclosure.category}-${disclosure.order}`}>
                <Flex direction="column" gap="3">
                    {/* Disclosure text with optional checkbox */}
                    <Flex direction="row" gap="3" alignItems="flex-start">
                        {disclosure.checkBox && (
                            <Checkbox
                                checked={isChecked}
                                onChange={(_, state) =>
                                    handleCheckboxChange(disclosure.order, state?.checked ?? false)
                                }
                                label={<HtmlContent content={sanitizedText} />}
                            />
                        )}
                    </Flex>

                    {/* Signature box if required */}
                    {disclosure.signatureBox && (
                        <SignatureCanvas
                            onSignatureChange={handleSignatureChange}
                            label="Sign here"
                            clearButtonText="Clear"
                        />
                    )}
                </Flex>
            </div>
        );
    };

    return (
        <Flex direction="column" gap="6">
            {/* Disclosures before signature section (checkboxes, info, etc.) */}
            {disclosuresData.disclosures.slice(0, firstSignatureIndex).map(renderDisclosure)}

            {/* Second-look co-application disclosure - positioned as the last checkbox, before signatures */}
            {showSecondLookDisclosure && (
                <SecondLookCoapplicationDisclosure onChange={setSecondLookDisclosureChecked} />
            )}

            {/* Signature disclosures */}
            {disclosuresData.disclosures.slice(firstSignatureIndex).map(renderDisclosure)}
        </Flex>
    );
};
